import React, { useState, useCallback, useRef, useEffect } from 'react'
import styles from './ChatbotFullView.module.scss'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import languages from '../languages.js'
import flags from '../flags.js'

const LANGUAGES = Object.entries(languages).map(([voice, label]) => ({
  voice,
  label,
  flag: flags[voice] ?? '🌐'
}))

export default function LocalChatbot({ voiceSpeed }) {
  const [input, setInput] = useState('')
  const [completions, setCompletions] = useState([])
  const [loading, setLoading] = useState(false)
  const [status, setStatusState] = useState({ type: 'idle', text: 'ready' })
  const [selectedLang, setSelectedLang] = useState(LANGUAGES[0])
  const [langOpen, setLangOpen] = useState(false)
  const inputRef = useRef(null)
  const debounceRef = useRef(null)
  const controllerRef = useRef(null)
  const langRef = useRef(null)

  const serverUrl = process.env.REACT_APP_OLLAMA_URI
  const model = 'qwen2.5:3b'

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const setStatus = (type, text) => setStatusState({ type, text })

  const handleInput = (e) => {
    const val = e.target.value
    setInput(val)
    clearTimeout(debounceRef.current)

    if (val.trim().length < 3) {
      setCompletions([])
      setStatus('idle', 'ready')
      return
    }

    setStatus('idle', 'typing…')
    debounceRef.current = setTimeout(() => fetchCompletions(val.trim()), 350)
  }

  const fetchCompletions = async (sentence) => {
    if (controllerRef.current) controllerRef.current.abort()
    controllerRef.current = new AbortController()

    setLoading(true)
    setStatus('active', 'generating…')
    const t0 = performance.now()

    const prompt = `You are an autocomplete engine. The user has typed the beginning of a sentence in ${selectedLang.label}. Complete it in 3 different short ways (max 5 words each). Be natural, varied, and concise. Respond in ${selectedLang.label}.

Respond ONLY with valid JSON in exactly this format, nothing else:
{"completions": ["completion one", "completion two", "completion three"]}

Sentence so far: "${sentence}"`

    try {
      const res = await fetch(`${serverUrl}/api/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_OLLAMA_KEY
        },
        signal: controllerRef.current.signal,
        body: JSON.stringify({
          model,
          prompt,
          stream: false,
          format: 'json',
          options: { temperature: 0.8, num_predict: 80 }
        })
      })

      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      const data = await res.json()
      const ms = Math.round(performance.now() - t0)
      const raw = data.response.replace(/```json|```/g, '').trim()
      const parsed = JSON.parse(raw)
      const results = parsed.completions || []

      if (results.length === 0) throw new Error('No completions returned')

      setCompletions(results)
      setStatus('idle', `${ms}ms · ${model}`)
    } catch (err) {
      if (err.name === 'AbortError') return
      setCompletions([])
      setStatus('error', `${err.message} — is Ollama running?`)
    }

    setLoading(false)
  }

  const applyCompletion = (idx) => {
    const completion = completions[idx]
    if (!completion) return
    const newVal = `${input.trim()} ${completion}`
    setInput(newVal)
    inputRef.current?.focus()
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchCompletions(newVal.trim()), 400)
  }

  const handleSpeak = useCallback(
    (text) => {
      speak(text, voices[selectedLang.voice], voiceSpeed)
    },
    [selectedLang, voiceSpeed]
  )

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setLangOpen(false)
      return
    }
    if (langOpen) return
    if (e.key === 'Enter' && completions.length > 0) {
      e.preventDefault()
      applyCompletion(0)
    }
    if (['1', '2', '3'].includes(e.key) && completions.length > 0) {
      applyCompletion(parseInt(e.key) - 1)
    }
  }

  const selectLang = (lang) => {
    setSelectedLang(lang)
    setLangOpen(false)
    setCompletions([])
    setStatus('idle', 'ready')
    inputRef.current?.focus()
  }

  return (
    <div className={styles.translationFullView}>
      <div className={styles.gridBg} aria-hidden="true" />

      <div className={styles.container}>
        {/* Header */}
        <header className={styles.header}>
          <div className={styles.headerTop}>
            <div>
              <div className={styles.label}>Mac Studio AI</div>
              <h1 className={styles.title}>
                Sentence <span>completion</span>
              </h1>
            </div>

            {/* Language selector */}
            <div className={styles.langSelector} ref={langRef}>
              <button className={`${styles.langBtn} ${langOpen ? styles.langBtnOpen : ''}`} onClick={() => setLangOpen((o) => !o)}>
                <span className={styles.langFlag}>{selectedLang.flag}</span>
                <span className={styles.langLabel}>{selectedLang.label}</span>
                <span className={`${styles.langChevron} ${langOpen ? styles.langChevronOpen : ''}`}>▾</span>
              </button>

              {langOpen && (
                <div className={styles.langDropdown}>
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.voice}
                      className={`${styles.langOption} ${lang.voice === selectedLang.voice ? styles.langOptionActive : ''}`}
                      onClick={() => selectLang(lang)}>
                      <span className={styles.langFlag}>{lang.flag}</span>
                      <span>{lang.label}</span>
                      {lang.voice === selectedLang.voice && <span className={styles.langCheck}>✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Input */}
        <div className={`${styles.inputWrap} ${loading ? styles.loading : ''}`}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            placeholder={`Start typing in ${selectedLang.label}…`}
            autoComplete="off"
            spellCheck="false"
          />
        </div>

        {/* Status bar */}
        <div className={`${styles.statusBar} ${styles[status.type] || ''}`}>
          <div className={styles.statusDot} />
          <span>{status.text}</span>
        </div>

        {/* Completions */}
        <div className={styles.responseArea}>
          {loading && (
            <div className={styles.thinking}>
              <span className={styles.dot} />
              <span className={styles.dot} />
              <span className={styles.dot} />
            </div>
          )}
          {!loading &&
            completions.length > 0 &&
            completions.map((c, i) => (
              <div key={i} className={styles.chip} onClick={() => applyCompletion(i)}>
                <span className={styles.chipNum}>{i + 1}</span>
                <span className={styles.chipPrefix}>{input.length > 30 ? '…' + input.slice(-27) : input}</span>
                <span className={styles.chipText}>{c}</span>
                <span className={styles.chipActions}>
                  <button
                    className={styles.speakBtn}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleSpeak(`${input} ${c}`)
                    }}
                    title="Speak">
                    ▶
                  </button>
                  <span className={styles.chipArrow}>↵</span>
                </span>
              </div>
            ))}
          {!loading && completions.length === 0 && <div className={styles.emptyState}>completions will appear here</div>}
        </div>

        {/* Hint */}
        <div className={styles.hint}>
          <span>
            <kbd className={styles.kbd}>↵</kbd> select first
          </span>
          <span>
            <kbd className={styles.kbd}>1</kbd>
            <kbd className={styles.kbd}>2</kbd>
            <kbd className={styles.kbd}>3</kbd> select by number
          </span>
          <span>▶ to speak</span>
        </div>

        {/* Config */}
        <div className={styles.config}>
          <div className={styles.configItem}>
            <span>model</span>
            <span className={styles.configValue}>{model}</span>
          </div>
          <div className={styles.configItem}>
            <span>endpoint</span>
            <span className={styles.configValue}>Mac Studio</span>
          </div>
          <div className={styles.configItem}>
            <span>voice</span>
            <span className={styles.configValue}>{selectedLang.voice}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
